/**
 * MarkdownTextarea
 *
 * Textarea com suporte a preview de Markdown em tempo real
 */
import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Eye, Edit3, HelpCircle } from 'lucide-react'

const MarkdownTextarea = ({
	label,
	value,
	onChange,
	placeholder,
	maxLength,
	rows = 6,
	className = '',
	helperText,
}) => {
	const [mode, setMode] = useState('edit') // 'edit' | 'preview' | 'split'

	const markdownStyles = `
		.markdown-preview h1 { font-size: 1.5em; font-weight: bold; margin-bottom: 0.5em; }
		.markdown-preview h2 { font-size: 1.3em; font-weight: bold; margin-bottom: 0.5em; }
		.markdown-preview h3 { font-size: 1.1em; font-weight: bold; margin-bottom: 0.5em; }
		.markdown-preview p { margin-bottom: 0.75em; }
		.markdown-preview strong { font-weight: bold; }
		.markdown-preview em { font-style: italic; }
		.markdown-preview ul { list-style-type: disc; margin-left: 1.5em; margin-bottom: 0.75em; }
		.markdown-preview ol { list-style-type: decimal; margin-left: 1.5em; margin-bottom: 0.75em; }
		.markdown-preview li { margin-bottom: 0.25em; }
		.markdown-preview hr { border: none; border-top: 1px solid rgba(255,255,255,0.2); margin: 1em 0; }
		.markdown-preview blockquote { border-left: 3px solid rgba(255,255,255,0.3); padding-left: 1em; margin: 0.75em 0; opacity: 0.8; }
		.markdown-preview code { background: rgba(255,255,255,0.1); padding: 0.2em 0.4em; border-radius: 3px; font-family: monospace; }
		.markdown-preview pre { background: rgba(255,255,255,0.1); padding: 1em; border-radius: 6px; overflow-x: auto; margin-bottom: 0.75em; }
		.markdown-preview a { color: #60a5fa; text-decoration: underline; }
	`

	return (
		<div className={className}>
			<style>{markdownStyles}</style>

			{/* Header com label e botões de modo */}
			<div className="flex items-center justify-between mb-1">
				<label className="block text-sm font-medium text-text-body">
					{label}
				</label>
				<div className="flex items-center gap-1">
					{/* Dica de Markdown */}
					<div className="relative group">
						<button
							type="button"
							className="p-1 text-text-muted hover:text-text rounded transition-colors"
							title="Dicas de formatação"
						>
							<HelpCircle className="w-4 h-4" />
						</button>
						<div className="absolute right-0 top-full mt-2 w-64 p-3 bg-base-300 border border-base-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
							<p className="text-xs font-semibold text-text mb-2">Formatação Markdown</p>
							<div className="text-xs text-text-muted space-y-1">
								<p><code className="bg-base-400 px-1 rounded">**texto**</code> → <strong>negrito</strong></p>
								<p><code className="bg-base-400 px-1 rounded">*texto*</code> → <em>itálico</em></p>
								<p><code className="bg-base-400 px-1 rounded">### Título</code> → Título</p>
								<p><code className="bg-base-400 px-1 rounded">* item</code> → Lista</p>
								<p><code className="bg-base-400 px-1 rounded">---</code> → Linha divisória</p>
								<p><code className="bg-base-400 px-1 rounded">`código`</code> → <code className="bg-base-400 px-1 rounded">código</code></p>
							</div>
						</div>
					</div>

					{/* Botões de modo */}
					<div className="flex bg-base-400 rounded-lg p-0.5">
						<button
							type="button"
							onClick={() => setMode('edit')}
							className={`px-2 py-1 text-xs rounded transition-colors ${
								mode === 'edit'
									? 'bg-primary text-white'
									: 'text-text-muted hover:text-text'
							}`}
							title="Editar"
						>
							<Edit3 className="w-3 h-3" />
						</button>
						<button
							type="button"
							onClick={() => setMode('split')}
							className={`px-2 py-1 text-xs rounded transition-colors ${
								mode === 'split'
									? 'bg-primary text-white'
									: 'text-text-muted hover:text-text'
							}`}
							title="Editar e Preview"
						>
							<span className="text-[10px]">50/50</span>
						</button>
						<button
							type="button"
							onClick={() => setMode('preview')}
							className={`px-2 py-1 text-xs rounded transition-colors ${
								mode === 'preview'
									? 'bg-primary text-white'
									: 'text-text-muted hover:text-text'
							}`}
							title="Preview"
						>
							<Eye className="w-3 h-3" />
						</button>
					</div>
				</div>
			</div>

			{/* Conteúdo baseado no modo */}
			{mode === 'edit' && (
				<textarea
					className="bg-base-300 w-full px-4 py-2 border border-base-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-text placeholder:text-text-muted resize-none font-mono text-sm"
					rows={rows}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					maxLength={maxLength}
				/>
			)}

			{mode === 'preview' && (
				<div
					className="bg-base-300 w-full px-4 py-3 border border-base-200 rounded-lg text-text overflow-auto"
					style={{ minHeight: `${rows * 1.5 + 1.5}rem` }}
				>
					{value ? (
						<div className="markdown-preview prose prose-invert prose-sm max-w-none">
							<ReactMarkdown>{value}</ReactMarkdown>
						</div>
					) : (
						<p className="text-text-muted italic">
							{placeholder || 'Nenhum conteúdo para visualizar'}
						</p>
					)}
				</div>
			)}

			{mode === 'split' && (
				<div className="grid grid-cols-2 gap-2">
					<textarea
						className="bg-base-300 w-full px-4 py-2 border border-base-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-text placeholder:text-text-muted resize-none font-mono text-sm"
						rows={rows}
						value={value}
						onChange={onChange}
						placeholder={placeholder}
						maxLength={maxLength}
					/>
					<div
						className="bg-base-300 w-full px-4 py-3 border border-base-200 rounded-lg text-text overflow-auto"
						style={{ minHeight: `${rows * 1.5 + 1.5}rem` }}
					>
						{value ? (
							<div className="markdown-preview prose prose-invert prose-sm max-w-none">
								<ReactMarkdown>{value}</ReactMarkdown>
							</div>
						) : (
							<p className="text-text-muted italic text-sm">
								Preview aparecerá aqui...
							</p>
						)}
					</div>
				</div>
			)}

			{/* Footer com contador e helper */}
			<div className="flex items-center justify-between mt-1">
				{helperText && (
					<p className="text-xs text-text-muted">{helperText}</p>
				)}
				{maxLength && (
					<p className="text-xs text-text-muted ml-auto">
						{value?.length || 0}/{maxLength}
					</p>
				)}
			</div>
		</div>
	)
}

export default MarkdownTextarea
